/**
 * ─── Module Management Settings ─────────────────────────────
 * Admin can toggle business modules ON/OFF at runtime.
 * Changes persist to localStorage and take effect immediately
 * (page reload shows updated sidebar).
 */

import { useState, useEffect, useCallback } from 'react'
import { Card, CardHeader, Badge, Button } from '@repo/ui-core'
import { featureFlags } from '@repo/feature-flags'
import type { FeatureFlag } from '@repo/feature-flags'
import {
  Settings2, ToggleLeft, ToggleRight, RotateCcw, RefreshCw,
  Building2, Shirt, Car, UtensilsCrossed, HeartPulse,
  Briefcase, Droplets, Swords, Gem, Landmark, Users,
} from 'lucide-react'

interface ModuleGroup {
  label: string
  icon: string
  flags: string[]
}

const MODULE_GROUPS: ModuleGroup[] = [
  {
    label: 'Cooperative ERP',
    icon: 'Building2',
    flags: ['module.cooperative', 'module.customer', 'module.multi-branch', 'module.changelog'],
  },
  {
    label: 'Laundry Shop',
    icon: 'Shirt',
    flags: ['module.laundry'],
  },
  {
    label: 'Driving School',
    icon: 'Car',
    flags: ['module.driving-school'],
  },
  {
    label: 'Fast Food',
    icon: 'UtensilsCrossed',
    flags: ['module.fastfood'],
  },
  {
    label: 'Clinic',
    icon: 'HeartPulse',
    flags: ['module.clinic'],
  },
  {
    label: 'HRIS',
    icon: 'Briefcase',
    flags: ['module.hris'],
  },
  {
    label: 'Water Station',
    icon: 'Droplets',
    flags: ['module.water-station'],
  },
  {
    label: 'Arena',
    icon: 'Swords',
    flags: ['module.arena'],
  },
  {
    label: 'Pawnshop',
    icon: 'Gem',
    flags: ['module.pawnshop'],
  },
  {
    label: 'Jewelry Shop',
    icon: 'Gem',
    flags: ['module.jewelry'],
  },
]

const GROUP_ICONS: Record<string, React.ComponentType<any>> = {
  Building2, Shirt, Car, UtensilsCrossed, HeartPulse,
  Briefcase, Droplets, Swords, Gem, Landmark, Users,
}

function GroupIcon({ name }: { name: string }) {
  const Icon = GROUP_ICONS[name] ?? Building2
  return <Icon className="h-5 w-5" />
}

export function ModuleSettingsPage() {
  const [flags, setFlags] = useState<FeatureFlag[]>([])
  const [message, setMessage] = useState('')

  const refresh = useCallback(() => {
    setFlags(featureFlags.getAll())
  }, [])

  useEffect(() => { refresh() }, [refresh])

  function toggle(key: string) {
    const flag = flags.find(f => f.key === key)
    if (!flag) return
    const newEnabled = !flag.enabled
    featureFlags.setEnabled(key, newEnabled)
    refresh()
    setMessage(`Module "${key}" ${newEnabled ? 'ENABLED' : 'DISABLED'}. Reload to update sidebar.`)
  }

  function resetAll() {
    if (!confirm('Reset all modules to their default settings?')) return
    const allFlags = featureFlags.getAll()
    for (const flag of allFlags) {
      featureFlags.resetToDefault(flag.key)
    }
    refresh()
    setMessage('All modules reset to defaults. Reload to apply.')
  }

  function handleReload() {
    window.location.reload()
  }

  const moduleFlags = flags.filter(f => f.key.startsWith('module.'))
  const enabledCount = moduleFlags.filter(f => f.enabled).length

  return (
    <div className="p-6">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Module Management</h2>
          <p className="mt-1 text-sm text-gray-500">
            {enabledCount} of {moduleFlags.length} modules enabled — toggle modules ON/OFF to control sidebar visibility
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={resetAll} icon={<RotateCcw className="h-4 w-4" />}>
            Reset All
          </Button>
          <Button onClick={handleReload} icon={<RefreshCw className="h-4 w-4" />}>
            Reload Page
          </Button>
        </div>
      </div>

      {message && (
        <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 p-3 flex items-center justify-between">
          <p className="text-sm text-blue-700">{message}</p>
          <button onClick={() => setMessage('')} className="text-xs text-blue-500 underline">Dismiss</button>
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {MODULE_GROUPS.map(group => {
          const groupFlags = group.flags
            .map(key => flags.find(f => f.key === key))
            .filter(Boolean) as FeatureFlag[]
          const allEnabled = groupFlags.every(f => f.enabled)
          const someEnabled = groupFlags.some(f => f.enabled)

          return (
            <Card key={group.label}>
              <div className="flex items-center gap-3 px-5 pt-5">
                <span className={`${allEnabled ? 'text-green-600' : someEnabled ? 'text-yellow-600' : 'text-gray-400'}`}>
                  <GroupIcon name={group.icon} />
                </span>
                <div className="flex-1">
                  <h3 className="text-base font-semibold text-gray-900">{group.label}</h3>
                </div>
                <Badge color={allEnabled ? 'green' : someEnabled ? 'yellow' : 'gray'}>
                  {allEnabled ? 'ON' : someEnabled ? 'Partial' : 'OFF'}
                </Badge>
              </div>
              <div className="divide-y divide-gray-100">
                {groupFlags.map(flag => (
                  <div key={flag.key} className="flex items-center justify-between p-4 hover:bg-gray-50">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {flag.description?.replace('Enable ', '') ?? flag.key}
                      </p>
                      <p className="text-xs text-gray-400 font-mono">{flag.key}</p>
                    </div>
                    <button
                      onClick={() => toggle(flag.key)}
                      className={`ml-3 relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 ${
                        flag.enabled ? 'bg-green-600' : 'bg-gray-300'
                      }`}
                      title={flag.enabled ? 'Click to disable' : 'Click to enable'}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          flag.enabled ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                ))}
              </div>
            </Card>
          )
        })}
      </div>

      {/* Infrastructure Flags */}
      <div className="mt-8">
        <h3 className="mb-4 text-lg font-semibold text-gray-900">Infrastructure Flags</h3>
        <Card>
          <div className="divide-y divide-gray-100">
            {flags.filter(f => !f.key.startsWith('module.')).map(flag => (
              <div key={flag.key} className="flex items-center justify-between p-4 hover:bg-gray-50">
                <div>
                  <p className="text-sm font-medium text-gray-900">{flag.description}</p>
                  <p className="text-xs text-gray-400 font-mono">{flag.key}</p>
                </div>
                <button
                  onClick={() => toggle(flag.key)}
                  className={`ml-3 relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    flag.enabled ? 'bg-green-600' : 'bg-gray-300'
                  }`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${flag.enabled ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  )
}
